/**
 * The counts on the filter bar's chips (Boards Redesign card 4.1).
 *
 * Each chip shows how many cards THAT chip alone would leave on the board —
 * the same thing Linear's and GitHub's filter menus show next to an option.
 * Counted over the cards the board shows with no filter (the board's "show
 * completed" toggle already applied, as for the "N of M shown" total), and
 * with the very predicates the filter uses (boardFilters.js), so a chip can
 * never say 3 and then show 4.
 *
 * Independent of the other chips on purpose: a count that shrank as you
 * picked other chips would make every chip look like it changed meaning, and
 * an active chip's count would just repeat "N of M shown".
 *
 * Pure: no DOM, no store; `now` is injectable for the Overdue rule.
 */

import { labelIdsOf } from './boardLabels';
import {
  PRIORITY_FILTER_LEVELS,
  matchesMine,
  matchesOverdue,
  matchesUnassigned,
  priorityOf,
} from './boardFilters';

/**
 * @param {object[]} tasks   the board's tasks as shown with no filter
 * @param {object}   ctx     { currentUserId, now }
 * @returns {{ overdue: number, mine: number, unassigned: number,
 *             labels: Record<string, number>, priority: Record<string, number> }}
 */
export function filterChipCounts(tasks, { currentUserId = null, now } = {}) {
  const counts = {
    overdue: 0,
    mine: 0,
    unassigned: 0,
    labels: {},
    priority: Object.fromEntries(PRIORITY_FILTER_LEVELS.map((p) => [p, 0])),
  };
  for (const t of tasks || []) {
    if (!t) continue;
    if (matchesOverdue(t, now)) counts.overdue += 1;
    if (matchesMine(t, currentUserId)) counts.mine += 1;
    if (matchesUnassigned(t)) counts.unassigned += 1;
    counts.priority[priorityOf(t)] += 1;
    // labelIdsOf de-duplicates, so a task holding an id twice counts once —
    // matchesLabels would show it once.
    for (const id of labelIdsOf(t)) counts.labels[id] = (counts.labels[id] || 0) + 1;
  }
  return counts;
}

/** The tasks a board shows with no filter: done cards drop out when the
 *  board hides completed ones. The denominator of "N of M shown". */
export function unfilteredTasks(tasks, showDoneTasks = true) {
  const list = tasks || [];
  return showDoneTasks ? list : list.filter((t) => !t.done);
}
